// utils/validators.js
export const isValidPhone = (phone) => /^[1-9]\d{9}$/.test(phone);

export const isValidNumberPlate = (plate) => {
  // Accept any non-empty plate. No format/regex check — every Indian series
  // (state, BH/Bharat, TEMP, and future formats) is allowed. Storage is
  // normalized server-side (uppercase + strip non-alphanumerics). This is a
  // presence check only.
  return (plate || '').trim().length > 0;
};

// ── Date / datetime bounds and validation ──────────────────────────────
// Native <input type="date"> accepts up to a 6-digit year (e.g. 202628)
// unless a `max` attribute caps it. These constants give every field a
// sane window, and the validators below are the submit-time guard since
// the control can still transiently hold an out-of-range typed value.

export const DATE_MIN = '1900-01-01';            // floor for DOB-style fields
export const DATE_MAX_FUTURE = '2100-12-31';     // ceiling for future dates
export const DATETIME_MAX_FUTURE = '2100-12-31T23:59';

export const todayISO = () => new Date().toISOString().split('T')[0];
export const nowDateTimeLocal = () => new Date().toISOString().slice(0, 16);

// A valid "YYYY-MM-DD" string within [min, max] (inclusive). Rejects
// 6-digit years because the length/regex check requires exactly 4.
export const isValidDateString = (val, { min = DATE_MIN, max = DATE_MAX_FUTURE } = {}) => {
  if (!val || typeof val !== 'string') return false;
  if (val.length !== 10 || !/^\d{4}-\d{2}-\d{2}$/.test(val)) return false;
  // Round-trip the parts: `new Date('2026-02-31')` silently rolls over to
  // Mar 3 instead of being invalid, so compare components to reject
  // impossible calendar dates (Feb 31, month 13, day 0, etc.).
  const [y, m, d] = val.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return false;
  return val >= min && val <= max; // ISO date strings sort lexicographically
};

// A valid "YYYY-MM-DDTHH:mm" datetime-local string within [min, max].
export const isValidDateTimeLocal = (val, { min, max = DATETIME_MAX_FUTURE } = {}) => {
  if (!val || typeof val !== 'string') return false;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(val)) return false;
  const d = new Date(val);
  if (isNaN(d.getTime())) return false;
  if (min && val < min) return false;
  return val <= max;
};

// Convenience for date-of-birth: 1900-01-01 .. today, not in the future.
export const isValidDob = (val) => isValidDateString(val, { min: DATE_MIN, max: todayISO() });
